import type { ComponentProps, ComponentType, MouseEvent, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { DisplayProps, PositionProps } from 'styled-system';
import { alignItems, display, justifyContent, position, right, themeGet, top } from 'styled-system';

import ClientOnly from 'common/components/ClientOnly';
import { Flex } from 'common/components/Flex';
import KeepFocusWithFirstTouch from 'common/components/KeepFocusWithFirstTouch';
import { Link } from 'common/components/Link';
import { CaptionRegularCR1, CaptionRegularCR2 } from 'common/components/Typography';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSSWithOffsetAndRadius } from 'common/theme/styles';
import { useTapToOpen } from '../../../hooks/useTapToOpen';
import { trackActionItemClick } from '../tracking';
import * as Icons from './ActionIcons';

interface ActionItemChildProps {
  shouldShowChildren: boolean;
  isActionItemActive: boolean;
}
export interface ActionItemProps {
  id: string;
  label: string;
  url: string;
  icon: string;
  count: number;
  countThreshold: number;
  isNewIcon?: boolean;
  showTooltip?: boolean;
  children?: (args: ActionItemChildProps) => ReactNode;
}

const ActionContainer = styled(KeepFocusWithFirstTouch)<PositionProps>`
  ${position};
`;

const iconContainerStyles: ComponentProps<typeof Flex> = {
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '28px',
  width: ['42px', null, '32px'],
  height: ['42px', null, '32px'],
  color: 'sys.neutral.border.hover',
};

const IconCounter = styled(CaptionRegularCR2)<any>`
  ${position};
  ${top};
  ${right};
  ${display};
  ${alignItems};
  ${justifyContent};
  min-width: 18px;
  height: 18px;
  color: white;
  border-radius: 100%;
  overflow: hidden;
`;

const iconCounterStyles = {
  as: 'span',
  position: 'absolute',
  top: ['3px', '-3px', '-4px'],
  right: ['2px', '-5px', '-9px'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bg: 'sys.primary.background.default',
};

const ActionItemLabel = styled(CaptionRegularCR1)<DisplayProps>`
  ${display};
`;

const actionItemLabelStyles: ComponentProps<typeof ActionItemLabel> = {
  as: 'span',
  display: ['none', null, 'block'],
};

const ActionLink = styled(Link)<{ disableHover?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  cursor: pointer;

  ${focusOutlineCSSWithOffsetAndRadius};

  &:hover > span {
    color: ${({ disableHover }) =>
      disableHover ? 'inherit' : themeGet('colors.sys.primary.text.hover')};
  }

  &:active > span {
    color: ${themeGet('colors.sys.primary.text.active')};
  }

  &:active svg {
    fill: ${themeGet('colors.sys.primary.icon.active')};
  }
`;

const ActionItem = ({
  label,
  url,
  icon,
  count,
  children,
  countThreshold = 1,
  showTooltip,
}: ActionItemProps) => {
  // INFO:
  // 1) Matching the media query allows us to disable
  //    ActionItem active state and children on mobile/tablet
  // 2) `defaultMatches` is set to false in order to avoid the default
  //    behaviour which is always match if you render on the server
  const {
    matches: { matchesTablet, matchesDesktop },
  } = useMatchesViewport(true, false, false);
  const { r, t, prefix } = useLocalization();
  const IconComponent = Icons[icon as keyof typeof Icons] as ComponentType;

  const isMobileOrTablet = !matchesDesktop;
  const shouldDisableHover = isMobileOrTablet && showTooltip;

  const tapToOpen = useTapToOpen(Boolean(shouldDisableHover && showTooltip));

  const isCountVisible = count >= countThreshold;
  const ariaLabel = isCountVisible
    ? `${t(label)}, ${r(t('h24_cart_items'), { totalArticlesCount: count })}`
    : '';

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>, focused: boolean) => {
    trackActionItemClick(t(label));
    tapToOpen.handleTap(event, focused);
  };

  return (
    <ActionContainer
      data-testid="action-item"
      active={shouldDisableHover || (matchesTablet && showTooltip)}
      disableHover={shouldDisableHover}
      position="relative"
      onFocusLost={tapToOpen.resetTapState}
    >
      {({ focused }) => (
        <>
          <ActionLink
            data-testid="action-item-link"
            aria-labelledby={`action-item-label-${label}`}
            href={prefix.t(url)}
            onClick={event => handleLinkClick(event, focused)}
            onBlur={() => tapToOpen.resetTapState()}
            styled={false}
            disableHover={shouldDisableHover}
          >
            <Flex aria-live="polite" aria-label={ariaLabel} as="span" {...iconContainerStyles}>
              <IconComponent key={icon} />
              <ClientOnly>
                {isCountVisible && (
                  <IconCounter
                    aria-hidden="true"
                    data-testid={`${icon}-badge-counter`}
                    {...iconCounterStyles}
                  >
                    {count}
                  </IconCounter>
                )}
              </ClientOnly>
            </Flex>
            <ActionItemLabel
              id={`action-item-label-${label}`}
              data-testid="action-item-label"
              {...actionItemLabelStyles}
            >
              {t(label)}
            </ActionItemLabel>
          </ActionLink>
          {typeof children === 'function' &&
            children({
              shouldShowChildren: shouldDisableHover ? focused : matchesTablet,
              isActionItemActive: focused,
            })}
        </>
      )}
    </ActionContainer>
  );
};

export default ActionItem;
